import { mappinGray, mappinGreen, mappinRed } from 'assets';
import { Button } from 'components';
import { googleService, MapStatus, SiteDirectorEnteringStatus } from 'constant';
import React, { FC, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { GoogleMap, InfoWindow, withGoogleMap, withScriptjs } from 'react-google-maps';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { GetMapDetailResponse } from '../../../types';
import { siteService } from '../../../services';
import SectionStyled, {
  MapComponentProps,
  MapProps,
  MarkerStyled,
  MarkerStyledProps,
} from './styles';

const MapComponent: FC<
  MapComponentProps & {
    defaultCenter?: { lat?: number | null; lng?: number | null };
    defaultZoom?: number;
  }
> = ({ listMarket, defaultCenter, defaultZoom = 10, ...props }) => {
  const FALLBACK_FUKUOKA = { lat: 34.94651411347855, lng: 137.0835973770727 };

  const sanitizeCenter = (
    dc: { lat?: number | null; lng?: number | null } | undefined,
    fallback: { lat: number; lng: number }
  ) => ({
    lat: typeof dc?.lat === 'number' ? dc!.lat : fallback.lat,
    lng: typeof dc?.lng === 'number' ? dc!.lng : fallback.lng,
  });
  const [searchCode, setSearchCode] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<number | null>(null);
  const scriptNonce = useRef<number>(Date.now());
  const avgCenter = useMemo(() => {
    const items = listMarket ?? [];
    const valid = items.filter(
      (v) => typeof v.site_lat === 'number' && typeof v.site_lng === 'number'
    );
    if (valid.length) {
      const lat = valid.reduce((s, v) => s + v.site_lat, 0) / valid.length;
      const lng = valid.reduce((s, v) => s + v.site_lng, 0) / valid.length;
      return { lat, lng };
    }
    return FALLBACK_FUKUOKA;
  }, [listMarket]);

  const [center, setCenter] = useState(() => sanitizeCenter(defaultCenter, avgCenter));
  const [zoom, setZoom] = useState<number>(defaultZoom);
  const [openNonce, setOpenNonce] = useState(0);

  useEffect(() => {
    if (selectedSite == null) {
      setCenter(sanitizeCenter(defaultCenter, avgCenter));
      setZoom(defaultZoom);
    }
  }, [
    defaultCenter?.lat,
    defaultCenter?.lng,
    avgCenter.lat,
    avgCenter.lng,
    defaultZoom,
    selectedSite,
  ]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef?.current || document.documentElement;
    const anyEl = el as any;
    if (!document.fullscreenElement) {
      (
        anyEl.requestFullscreen ||
        anyEl.webkitRequestFullscreen ||
        anyEl.mozRequestFullScreen ||
        anyEl.msRequestFullscreen
      )?.call(el);
    } else {
      (
        document.exitFullscreen ||
        (document as any).webkitExitFullscreen ||
        (document as any).mozCancelFullScreen ||
        (document as any).msExitFullscreen
      )?.call(document);
    }
  }, []);
  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let text = e.target.value
        .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0)) // 全角→半角
        .replace(/\D/g, ''); // 数字以外削除
      setSearchCode(text);
      setErrorMsg('');

      if (text.length !== 7) {
        if (text.length === 0) setSelectedSite(null);
        return;
      }

      if (text.length === 7) {
        if (!/^[0-9]{7}$/.test(text)) {
          setErrorMsg('数字7桁で入力してください');
          return;
        }
        const target = listMarket?.find((item) => (item.building_code ?? '').slice(0, 7) === text);
        if (!target) {
          setErrorMsg('該当する現場が存在しません。');
        } else {
          setErrorMsg('');
          setSelectedSite(target.site_id);
          setCenter({ lat: target.site_lat, lng: target.site_lng });
          setZoom(16);
          setOpenNonce((n) => n + 1);
        }
      }
    },
    [listMarket]
  );
  return (
    <SectionStyled>
      <div
        ref={wrapperRef}
        style={{ position: 'relative', height: `calc(100vh - 112px)`, width: '100%' }}
      >
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 80,
            background: '#fff',
            padding: '8px',
            borderRadius: 4,
            zIndex: 10,
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
        >
          <input
            value={searchCode}
            onChange={handleSearch}
            placeholder="受注コード(7桁)"
            maxLength={7}
            style={{
              width: 160,
              padding: '4px 6px',
              border: '1px solid #ccc',
              borderRadius: 4,
              background: '#fff',
              color: '#000',
            }}
          />
          {errorMsg && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errorMsg}</div>}
        </div>
        <button
          onClick={toggleFullscreen}
          className="gmfs-btn"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 10000,
          }}
          aria-label={isFs ? '全屏を終了' : '全屏'}
          title={isFs ? '全屏を終了' : '全屏'}
        >
          {!isFs ? (
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M3 9V3h6v2H5v4H3zm12-6h6v6h-2V5h-4V3zM3 15h2v4h4v2H3v-6zm16 0h2v6h-6v-2h4v-4z"
                fill="currentColor"
              />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M7 7h4V5H5v6h2V7zm6 0h4v4h2V5h-6v2zm-6 6H5v6h6v-2H7v-4zm10 0v4h-4v2h6v-6h-2z"
                fill="currentColor"
              />
            </svg>
          )}
        </button>
        <style>
          {`
  .gmfs-btn{
    width:40px;height:40px;
    padding:0;border:0;border-radius:2px;
    background:#fff; cursor:pointer;
    display:grid; place-items:center;
    box-shadow:0 1px 4px rgba(0,0,0,.3);
  }
  .gmfs-btn:hover{ background:#f5f5f5; }
  .gmfs-btn:active{ background:#eee; }
  .gmfs-btn:focus{
    outline:none;
    box-shadow:0 0 0 2px rgba(26,115,232,.3), 0 1px 4px rgba(0,0,0,.3);
  }
`}
        </style>
        <MapRender
          center={center}
          zoom={zoom}
          options={{ fullscreenControl: false, gestureHandling: 'greedy' }}
          googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${googleService.API_KEY}&language=ja&region=JP&v=weekly&_=${scriptNonce.current}`}
          loadingElement={<div className="loading_element" style={{ height: `100%` }} />}
          containerElement={
            <div
              className="container_element"
              style={{ height: `calc(100vh - 112px)`, width: '100%', maxWidth: '100%' }}
            />
          }
          mapElement={<div className="map_element" style={{ height: `100%` }} />}
          listMarket={listMarket}
          selectedSite={selectedSite}
          openNonce={openNonce}
          {...props}
        />
      </div>
    </SectionStyled>
  );
};
const Map: FC<
  MapProps & {
    selectedSite?: number | null;
    center: { lat: number; lng: number };
    openNonce?: number;
  }
> = ({ listMarket = [], selectedSite, center, openNonce, ...props }) => {
  return (
    <GoogleMap {...props} center={center}>
      {listMarket &&
        listMarket.length > 0 &&
        listMarket.map((item) => {
          return (
            <MarkerCustom
              key={item.site_id}
              dataItem={item}
              isSelected={item.site_id === selectedSite}
              openNonce={item.site_id === selectedSite ? openNonce : 0}
            />
          );
        })}
    </GoogleMap>
  );
};

const MarkerCustom: FC<MarkerStyledProps & { isSelected: boolean; openNonce?: number }> = ({
  dataItem,
  isSelected,
  openNonce = 0,
}) => {
  const [showInfo, setShowInfo] = useState<boolean>(isSelected);
  const [listStatusItem, setStatus] = useState<GetMapDetailResponse>();
  const history = useHistory();

  useEffect(() => {
    let raf1: number | null = null;
    let raf2: number | null = null;

    if (isSelected && dataItem) {
      siteService
        .getMapDetail({ site_id: dataItem.site_id, date: dayjs().format('YYYY-MM-DD') })
        .then(({ data }) => setStatus(data));

      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShowInfo(true));
      });
    } else {
      setShowInfo(false);
    }

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isSelected, dataItem?.site_id, openNonce]);

  const handleClickPush = (site_id: number | undefined, building_id: number | undefined) => {
    history.push(`/site/${site_id}/building/${building_id}`);
  };
  const googleMapSite = (value: number | undefined) => {
    const site_lat = dataItem?.site_id === value && dataItem?.site_lat;
    const site_lng = dataItem?.site_id === value && dataItem?.site_lng;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${site_lat},${site_lng}`,
      '_blank'
    );
  };
  const StatusOfSiteDirectorEntering = useCallback(
    ({
      status,
      site_director_name,
    }: {
      status: GetMapDetailResponse['status_of_site_director_entering'];
      site_director_name: GetMapDetailResponse['site_director_name'];
    }) => {
      switch (status) {
        case SiteDirectorEnteringStatus.COME_IN:
          return site_director_name ?? '-';
        case SiteDirectorEnteringStatus.REPLACEMENT_STAFF:
          return '代行社員';
        default:
          return '-';
      }
    },
    []
  );
  return (
    <MarkerStyled
      visible={true}
      onClick={async () => {
        setShowInfo(true);
        if (dataItem?.site_id) {
          const { data } = await siteService.getMapDetail({
            site_id: dataItem.site_id,
            date: dayjs().format('YYYY-MM-DD'),
          });
          setStatus(data);
        }
      }}
      icon={{
        url: `${
          (dataItem?.status_active === MapStatus.WORKING && mappinGreen) ||
          (dataItem?.status_active === MapStatus.REST && mappinRed) ||
          (dataItem?.status_active === MapStatus.NOT_START && mappinGray)
        }
         `,
        scaledSize: new window.google.maps.Size(26, 40),
      }}
      label={{
        text: `${
          dataItem?.status_active === MapStatus.NOT_START ? '未' : dataItem?.number_of_workers
        }`,
        className: 'label-custom',
        color: '#FFFFFF',
        fontFamily: 'Noto Sans JP',
        fontWeight: '400',
        fontSize: '16px',
      }}
      position={{ lat: dataItem?.site_lat, lng: dataItem?.site_lng }}
    >
      {showInfo && (
        <InfoWindow key={`iw-${dataItem?.site_id}-${openNonce}`}>
          {listStatusItem ? (
            <div className="container-info-windown">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 'bold',
                }}
              >
                <div
                  className={`status ${
                    dataItem?.status_active === MapStatus.WORKING && 'working'
                  } ${dataItem?.status_active === MapStatus.REST && 'rest'} ${
                    dataItem?.status_active === MapStatus.NOT_START && 'not_start'
                  }`}
                >
                  {(dataItem?.status_active === MapStatus.WORKING && '作業中') ||
                    (dataItem?.status_active === MapStatus.REST && '休止中') ||
                    (dataItem?.status_active === MapStatus.NOT_START && '未着工')}
                </div>
                {listStatusItem?.building_code && (
                  <span style={{ color: 'black' }}>{listStatusItem.building_code}</span>
                )}
              </div>
              <div className="title">{listStatusItem?.site_name}</div>
              <div className="text-content technician">
                工事担当者：{listStatusItem?.site_director_name}
              </div>
              {dataItem?.status_active !== MapStatus.NOT_START && (
                <>
                  <div className=" text-content admission">
                    入場中の担当者：
                    {StatusOfSiteDirectorEntering({
                      status: listStatusItem?.status_of_site_director_entering,
                      site_director_name: listStatusItem?.site_director_name,
                    })}
                  </div>
                  <div className=" text-content worker">
                    作業中の人員：{dataItem?.number_of_workers}人
                  </div>
                </>
              )}
              <div className="box_button">
                <Button
                  size="small"
                  style={{ minWidth: ' 104.06px' }}
                  onClick={() => {
                    handleClickPush(dataItem?.site_id, dataItem?.building_id);
                  }}
                  className="btn_detail mr-7"
                  disabled={
                    (listStatusItem && listStatusItem?.flag_of_detail_button === 0) ||
                    !listStatusItem
                  }
                >
                  現場詳細を見る
                </Button>
                <Button
                  onClick={() => googleMapSite(dataItem?.site_id)}
                  size="small"
                  style={{ minWidth: ' 104.06px' }}
                  className="btn_detail_map ml-7"
                >
                  詳細マップを見る
                </Button>
              </div>
            </div>
          ) : (
            <div className="skeleton" style={{ width: '248px', height: '125px' }} />
          )}
        </InfoWindow>
      )}
    </MarkerStyled>
  );
};

const MapRender = withScriptjs(withGoogleMap(Map));
export default MapComponent;
